import { saveAs } from 'file-saver';
import ky from 'ky';
import { useEffect, useState } from 'react';
import { browserName, isFirefox } from 'react-device-detect';

import { SpinningIcon } from '@/components/common/spinning-icon';
import { Button, ButtonVariant } from '@/components/form/button';
import { SampleImages } from '@/features/demo/base64-sample-images/beer';
import { checkSharpness } from '@/features/single-measurement/services/sharpness';
import { useRoute } from '@/hooks/use-route';

type State = 'in_progress' | 'success' | 'error';

interface ITroubleshootItem {
  log: string;
  state: State;
  description: string;
}

const initialState: ITroubleshootItem = {
  log: '',
  state: 'in_progress',
  description: '',
};

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringify the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

const prefixURL = import.meta.env.VITE_API_BASE_URL;
const middlewareUrl = import.meta.env.VITE_MIDDLEWARE_BASE_URL;

const videoConstraints = isFirefox ? { height: 1080 } : { width: 9999 };

export const DebugPage = () => {
  const { routeToHome } = useRoute();
  const [operatingSystem, setOperatingsystem] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Operating system',
  });

  const [browser, setBrowser] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Browser',
  });

  const [server, setServer] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Server connectivity',
  });

  const [camera, setCamera] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Camera devices',
  });

  const [middleware, setMiddleware] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Middleware',
  });

  const [sharpnessCheck, setSharpnessCheck] = useState<ITroubleshootItem>({
    ...initialState,
    description: 'Image validation (sharpness check)',
  });

  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  useEffect(() => {
    // Browser & OS
    try {
      const log = `${navigator.userAgent} ${browserName}`;
      setBrowser((oldVal) => {
        return { ...oldVal, state: 'success', log };
      });
      setOperatingsystem((oldVal) => {
        return { ...oldVal, state: 'success', log };
      });
    } catch (error) {
      setBrowser((oldVal) => {
        return { ...oldVal, state: 'error', log: getErrorMessage(error) };
      });
    }

    // middleware connectivity
    ky.get(`${middlewareUrl}`)
      .then((payload: Response) => {
        const { url, type, status, redirected } = payload;
        let log = JSON.stringify({ url, type, status, redirected });
        payload.text().then((body) =>
          setMiddleware((oldVal) => ({
            ...oldVal,
            state: 'success',
            log: `${log} ${body}`,
          })),
        );
      })
      .catch((error) => {
        setMiddleware((oldVal) => {
          return { ...oldVal, state: 'error', log: getErrorMessage(error) };
        });
      });

    // server connectivity
    ky.get(`${prefixURL}/health`)
      .then((payload: Response) => {
        const { url, type, status, redirected } = payload;
        let log = JSON.stringify({ url, type, status, redirected });
        payload.text().then((body) =>
          setServer((oldVal) => ({
            ...oldVal,
            state: 'success',
            log: `${log} ${body}`,
          })),
        );
      })
      .catch((error) => {
        setServer((oldVal) => {
          return { ...oldVal, state: 'error', log: getErrorMessage(error) };
        });
      });

    // sharpness check
    checkSharpness(SampleImages[0])
      .then((d: any) => {
        const log = JSON.stringify({ ...d, imageString: '' });
        setSharpnessCheck((oldVal) => ({
          ...oldVal,
          state: 'success',
          log: `${log}`,
        }));
      })
      .catch((error) => {
        setSharpnessCheck((oldVal) => {
          return { ...oldVal, state: 'error', log: getErrorMessage(error) };
        });
      });

    // camera
    let cameraLog: string[] = [];
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter(({ kind }) => kind === 'videoinput');
        for (const device of videoDevices) {
          cameraLog.push(JSON.stringify(device));
          navigator.mediaDevices
            .getUserMedia({ video: { ...videoConstraints, deviceId: device.deviceId } })
            .then((stream) => {
              const tracks = stream.getVideoTracks();
              cameraLog.push(JSON.stringify(tracks[0].getSettings()));
              setCamera((oldVal) => {
                return { ...oldVal, state: 'success', log: cameraLog.toString() };
              });
            })
            .catch((error) => {
              cameraLog.push(getErrorMessage(error));
              setCamera((oldVal) => {
                return { ...oldVal, state: 'error', log: cameraLog.toString() };
              });
              cameraLog.push(getErrorMessage(error));
            });
        }
      })
      .catch((err) => {
        cameraLog.push(getErrorMessage(err));
        setCamera((oldVal) => {
          return { ...oldVal, state: 'error', log: cameraLog.toString() };
        });
      });
  }, []);

  useEffect(() => {
    const states: ITroubleshootItem[] = [operatingSystem, browser, camera, server];

    const isPending = states.find(({ state }) => state === 'in_progress');

    if (!isPending) setIsCompleted(true);
  }, [operatingSystem, browser, camera, server]);

  const downloadLogFile = () => {
    const text = `
    Operating System: ${operatingSystem.log} \n
    Browser: ${browser.log} \n
    Camera: ${camera.log} \n
    Server: ${server.log} \n
    Middleware: ${middleware.log} \n
    Sharpness Check: ${sharpnessCheck.log} \n
    `;
    var file = new File(
      [text],
      `Oculyze Report - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}.txt`,
      {
        type: 'text/plain;charset=utf-8',
      },
    );
    saveAs(file);
  };

  return (
    <div className="relative px-4 mb-auto md:px-0 md:container md:mx-auto">
      <div className="pt-24 md:pb-24">
        <h1 className="uppercase sm:mx-0.5 lg:mx-0.5 text-2xl">Troubleshoot</h1>
        <div className="flex flex-col">
          <div className="overflow-x-auto sm:mx-0.5 lg:mx-0.5">
            <div className="py-2 inline-block min-w-full">
              <div className="overflow-hidden">
                <table className="min-w-full">
                  <tbody>
                    <Row
                      desc={operatingSystem.description}
                      state={operatingSystem.state}
                    />
                    <Row desc={browser.description} state={browser.state} />
                    <Row desc={server.description} state={server.state} />
                    <Row desc={middleware.description} state={middleware.state} />
                    <Row desc={camera.description} state={camera.state} />
                    <Row desc={sharpnessCheck.description} state={sharpnessCheck.state} />
                  </tbody>
                </table>
                <div className="flex justify-end mt-10 gap-5">
                  <Button variant={ButtonVariant.Secondary} onClick={routeToHome}>
                    Back to home
                  </Button>
                  <Button onClick={downloadLogFile} isLoading={!isCompleted}>
                    Download Report
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ desc, state }: { desc: string; state: State }) => {
  return (
    <tr className="bg-white border-b transition duration-300 ease-in-out hover:bg-gray-100">
      <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap">
        {desc}
      </td>
      <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap flex justify-end">
        {state === 'in_progress' ? <SpinningIcon color="fill-wine-primary" /> : null}
        {state === 'success' ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6 text-[#16a34a]"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : null}
        {state === 'error' ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6 text-[#dc2626]"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        ) : null}
      </td>
    </tr>
  );
};
